import React, { useEffect, useRef, useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage, auth } from '../firebaseConfig';
import { MAX_IMAGE_COMPONENT_IMAGES, normalizeImageGallery } from '../utils/imageGallery';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const uploadSingleImageFile = (file, uid) =>
    new Promise((resolve, reject) => {
        const path = `images/${uid}/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, path);
        const task = uploadBytesResumable(storageRef, file);
        task.on(
            'state_changed',
            () => {},
            (err) => reject(err),
            async () => {
                try {
                    const url = await getDownloadURL(task.snapshot.ref);
                    resolve(url);
                } catch (e) {
                    reject(e);
                }
            }
        );
    });

const createInitialImages = (initialImages = [], initialNsfa = false) => {
    const normalized = normalizeImageGallery({ images: initialImages, nsfa: initialNsfa }, { nsfa: initialNsfa });
    return normalized.map((item, index) => ({
        id: `existing-${index}-${item.url}`,
        url: item.url,
        previewUrl: item.url,
        file: null,
        alt: item.alt || '',
        caption: item.caption || '',
        nsfa: !!item.nsfa,
        isLocal: false,
    }));
};

export default function NewImageComponentForm({
                                                  alt = '',
                                                  caption = '',
                                                  onAltChange,
                                                  onCaptionChange,
                                                  onUploadComplete,
                                                  enablePublishButton = false,
                                                  onSubmitAndPublish,
                                                  onCancel,
                                                  error,
                                                  initialNsfa = false,
                                                  onNsfaChange,
                                                  initialImages = [],
    onMediaCountChange,
                                              }) {
    const [images, setImages] = useState(() => createInitialImages(initialImages, initialNsfa));
    const [uploading, setUploading] = useState(false);
    const [nsfa, setNsfa] = useState(!!initialNsfa);
    const [localError, setLocalError] = useState('');
    const imagesRef = useRef(images);
    const initialImagesRef = useRef(initialImages);

    useEffect(() => { onMediaCountChange?.(images.length); }, [images.length, onMediaCountChange]);

    useEffect(() => {
        imagesRef.current = images;
    }, [images]);

    useEffect(() => {
        setNsfa(!!initialNsfa);
    }, [initialNsfa]);

    useEffect(() => {
        if (initialImagesRef.current === initialImages) return;
        initialImagesRef.current = initialImages;
        (imagesRef.current || []).forEach((item) => {
            if (item?.isLocal && typeof item.previewUrl === 'string' && item.previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(item.previewUrl);
            }
        });
        setImages(createInitialImages(initialImages, initialNsfa));
        setLocalError('');
    }, [initialImages, initialNsfa]);

    useEffect(() => {
        return () => {
            (imagesRef.current || []).forEach((item) => {
                if (item?.isLocal && typeof item.previewUrl === 'string' && item.previewUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(item.previewUrl);
                }
            });
        };
    }, []);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        setLocalError('');
        if (selectedFiles.length === 0) return;

        const availableSlots = MAX_IMAGE_COMPONENT_IMAGES - images.length;
        if (availableSlots <= 0) {
            const msg = `You can upload up to ${MAX_IMAGE_COMPONENT_IMAGES} images per gallery.`;
            setLocalError(msg);
            alert(msg);
            e.target.value = '';
            return;
        }
        if (selectedFiles.length > availableSlots) {
            const msg = `You can add ${availableSlots} more image${availableSlots === 1 ? '' : 's'} (max ${MAX_IMAGE_COMPONENT_IMAGES}).`;
            setLocalError(msg);
            alert(msg);
            e.target.value = '';
            return;
        }

        const tooLarge = selectedFiles.find((selected) => selected.size > MAX_FILE_SIZE);
        if (tooLarge) {
            const msg = `File is too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)} MB.`;
            setLocalError(msg);
            alert(msg);
            e.target.value = '';
            return;
        }

        const startIndex = images.length;
        const nextItems = selectedFiles.map((selected, idx) => ({
            id: `local-${Date.now()}-${idx}-${selected.name}`,
            url: '',
            previewUrl: URL.createObjectURL(selected),
            file: selected,
            alt: alt || `Gallery image ${startIndex + idx + 1}`,
            caption: '',
            nsfa: !!nsfa,
            isLocal: true,
        }));
        setImages((prev) => [...prev, ...nextItems]);
        e.target.value = '';
    };

    const removeImageAt = (removeIndex) => {
        setImages((prev) => {
            const next = [...prev];
            const removed = next.splice(removeIndex, 1)[0];
            if (removed?.isLocal && typeof removed.previewUrl === 'string' && removed.previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(removed.previewUrl);
            }
            return next;
        });
    };

    const toggleNsfa = (checked) => {
        setNsfa(checked);
        onNsfaChange?.(checked);
    };

    const performUpload = async () => {
        setLocalError('');
        if (!images.length) {
            const msg = 'Please select at least one image to upload.';
            setLocalError(msg);
            throw new Error('missing-file');
        }

        setUploading(true);
        try {
            const uid = auth.currentUser?.uid || 'anonymous';
            const uploadedImages = [];

            for (let idx = 0; idx < images.length; idx += 1) {
                const item = images[idx];
                let url = item?.url || '';
                if (item?.file) {
                    url = await uploadSingleImageFile(item.file, uid);
                }
                if (!url) continue;

                const fallbackAlt = alt || `Gallery image ${idx + 1}`;
                const fallbackCaption = idx === 0 ? (caption || '') : '';
                const nextAlt = idx === 0
                    ? (alt || item?.alt || fallbackAlt)
                    : (item?.alt || fallbackAlt);
                const nextCaption = idx === 0
                    ? (caption || item?.caption || fallbackCaption)
                    : (item?.caption || fallbackCaption);
                uploadedImages.push({
                    url,
                    alt: nextAlt,
                    caption: nextCaption,
                    nsfa: !!nsfa,
                });
            }

            if (!uploadedImages.length) {
                const msg = 'No image URLs were generated during upload.';
                setLocalError(msg);
                throw new Error('missing-urls');
            }

            const first = uploadedImages[0];
            return {
                url: first.url,
                alt: first.alt || alt || '',
                caption: first.caption || caption || '',
                nsfa: !!nsfa,
                images: uploadedImages,
            };
        } finally {
            setUploading(false);
        }
    };

    const handleUpload = async () => {
        try {
            const { url, alt: a, caption: c, gate, nsfa: n, images: galleryImages } = await performUpload();
            onUploadComplete(url, a ?? alt, c ?? caption, gate, n, galleryImages);
        } catch (e) {
            if (!localError) {
                setLocalError('Image upload failed.');
            }
            console.error(e);
        }
    };

    const handleUploadAndPublish = async () => {
        try {
            const { url, alt: a, caption: c, gate, nsfa: n, images: galleryImages } = await performUpload();
            onSubmitAndPublish?.(url, a ?? alt, c ?? caption, gate, n, galleryImages);
        } catch (e) {
            if (!localError) {
                setLocalError('Image upload failed.');
            }
            console.error(e);
        }
    };

    return (
        <div className="new-component-form image-form">
            <h1>Image Component Form</h1>

            <label style={{ display: 'block', marginBottom: 8 }}>
                Alt text (for accessibility)
                <input
                    type="text"
                    placeholder="Describe the image for screen readers"
                    value={alt}
                    onChange={(e) => onAltChange?.(e.target.value)}
                    className="muulti-input"
                />
            </label>

            <label style={{ display: 'block', marginBottom: 12 }}>
                Caption (optional)
                <textarea
                    placeholder="Add a short caption"
                    value={caption}
                    onChange={(e) => onCaptionChange?.(e.target.value)}
                    className="muulti-textarea"
                    rows={3}
                />
            </label>

            <h3>Upload images (max {MAX_IMAGE_COMPONENT_IMAGES})</h3>
            <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                className="muulti-input"
            />

            <p style={{ marginTop: 8, marginBottom: 8 }}>
                {images.length} / {MAX_IMAGE_COMPONENT_IMAGES} selected
            </p>

            {images.length > 0 && (
                <div className="image-form-preview-grid">
                    {images.map((item, idx) => (
                        <div key={item.id} className="image-form-preview-item">
                            <img
                                src={item.previewUrl || item.url}
                                alt={item.alt || `Selected image ${idx + 1}`}
                                className="image-form-preview-image"
                            />
                            <button
                                type="button"
                                className="muulti-button image-form-remove-button"
                                onClick={() => removeImageAt(idx)}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0' }}>
                <input
                    type="checkbox"
                    checked={nsfa}
                    onChange={(e) => toggleNsfa(e.target.checked)}
                />
                Mark as NSFA (blur by default)
            </label>

            {(localError || error) && (
                <p className="error" style={{ marginBottom: 12 }}>
                    {localError || error}
                </p>
            )}

            <div className="form-buttons">
                <button
                    onClick={handleUpload}
                    disabled={!images.length || uploading}
                    className="muulti-button"
                    type="button"
                >
                    {uploading ? 'Uploading...' : 'Save Panel'}
                </button>

                {enablePublishButton && (
                    <button
                        onClick={handleUploadAndPublish}
                        disabled={!images.length || uploading}
                        className="muulti-button"
                        type="button"
                    >
                        {uploading ? 'Uploading...' : 'Save Panel & Publish'}
                    </button>
                )}

                <button onClick={onCancel} className="muulti-button cancel-button" type="button">
                    Cancel
                </button>
            </div>
        </div>
    );
}
